import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import { Icon, Modal as ModalComponent, VideoPlayer } from 'src/components';
import { Color } from 'src/utils/theme';

/* istanbul ignore next: tested visually */
export const Modal = styled(ModalComponent)<{ $hasVideo: boolean }>(
  ({ theme, $hasVideo }) => css`
    .reactModalOverlay {
      background: ${transparentize(0.5, theme.co.secondary10)};
      backdrop-filter: blur(${theme.sz.s4});
      padding: ${theme.sz.s4};
    }

    .reactModalContent {
      max-width: ${$hasVideo ? theme.sz.lg : theme.sz.ms};
      margin: 0;
      padding: ${theme.sz.s6};
      border-radius: ${theme.bo.large};
      box-shadow: ${theme.sh.s5};

      @media (max-width: ${theme.sz.lg}) {
        max-width: ${theme.sz.ms};
        padding: ${theme.sz.s4};
        border-radius: ${theme.bo.medium};
      }
    }
  `,
);

export const Layout = styled.div<{ $hasVideo: boolean }>(
  ({ theme, $hasVideo }) => css`
    display: grid;
    grid-template-columns: ${$hasVideo ? '1fr 1fr' : '1fr'};
    align-items: stretch;
    gap: ${theme.sz.s6};

    @media (max-width: ${theme.sz.lg}) {
      grid-template-columns: 1fr;
      gap: ${theme.sz.s4};
    }
  `,
);

export const Stage = styled.div(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;
    border-radius: ${theme.bo.medium};
    background: ${theme.co.primary90};
    min-height: ${theme.sz.xs};

    /* Stacked on small screens, a tall clip pushes the CTA below the fold. */
    @media (max-width: ${theme.sz.lg}) {
      min-height: auto;
      aspect-ratio: 4 / 3;
    }
  `,
);

export const Clip = styled(VideoPlayer)(
  () => css`
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      border-radius: 0;
      object-fit: cover;
    }
  `,
);

export const Phrase = styled.p(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    position: absolute;
    bottom: ${theme.sz.s4};
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100% - ${theme.sz.s8});
    margin: ${theme.sz.s0};
    padding: ${theme.sz.s2} ${theme.sz.s4};
    border-radius: ${theme.bo.round};
    background: ${transparentize(0.15, theme.co.neutral20)};
    color: ${theme.co.neutral99};
    text-align: center;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const Details = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s5};

    @media (max-width: ${theme.sz.lg}) {
      gap: ${theme.sz.s4};
    }
  `,
);

export const Banner = styled.div(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxs};
    display: flex;
    align-items: center;
    gap: ${theme.sz.s3};
    padding: ${theme.sz.s3} ${theme.sz.s4};
    border-radius: ${theme.bo.medium};
    background: ${theme.co.secondary10};
    color: ${theme.co.neutral99};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xxs};
    }
  `,
);

export const BannerIcon = styled(Icon)(
  ({ theme }) => css`
    flex-shrink: 0;
    font-size: ${theme.sz.s7};
    color: ${theme.co.primary40};
  `,
);

export const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.m};
    color: ${theme.co.secondary0};
    margin: ${theme.sz.s0};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.m};
    }
  `,
);

export const Description = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary50};
    margin: ${theme.sz.s0};
  `,
);

export const PlanRow = styled.div(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${theme.sz.s4};
    padding: ${theme.sz.s4} ${theme.sz.s0};
    border-top: ${theme.bo.thinWidth} solid ${theme.co.neutral90};
    border-bottom: ${theme.bo.thinWidth} solid ${theme.co.neutral90};
    color: ${theme.co.secondary0};
  `,
);

export const SocialProof = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    display: flex;
    align-items: center;
    gap: ${theme.sz.s3};
    color: ${theme.co.secondary20};
  `,
);

export const Avatars = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    padding-left: ${theme.sz.s2};
  `,
);

export const Avatar = styled.span<{ $color: Color }>(
  ({ theme, $color }) => css`
    ${theme.typography.heading.desktop.xxxs};
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s7};
    height: ${theme.sz.s7};
    margin-left: -${theme.sz.s2};
    border: ${theme.bo.mediumWidth} solid ${theme.co.neutral99};
    border-radius: ${theme.bo.elliptical};
    background: ${theme.co[$color]};
    color: ${theme.co.neutral99};
  `,
);

export const Actions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
  `,
);

export const FreeLink = styled.button(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    display: block;
    width: 100%;
    padding: ${theme.sz.s2} ${theme.sz.s0};
    border: none;
    background: none;
    color: ${theme.co.secondary50};
    font-weight: 500;
    text-align: center;
    cursor: pointer;
  `,
);
